import { useQuery } from '@tanstack/react-query';

import {
  RequestBusinessPartnerStatusEnum,
  ServiceAgreementStatusEnum,
} from '../../../api/generated/pm';
import {
  getV1RequestBusinessPartnerProxiesOptions,
  getV1ServiceAgreementsWithCustomerOptions,
} from '../../../api/generated/pm/@tanstack/react-query.gen';
import type { ListQueryParams } from '../../../hooks/useListState';

/**
 * Query hooks for the Requests page's two tabs (PRD 04 "Data", D1): Create
 * requests (open proxy Business Partner Requests) and Join requests
 * (outgoing pending/declined Service Agreements with existing Customers).
 * Colocated with the page because it alone owns these two endpoints and
 * their status sets.
 */

/** `accepted` / `done` are excluded: those proxies are becoming Customers. */
const CREATE_REQUESTS_STATUSES = [
  RequestBusinessPartnerStatusEnum.CUSTOMER_REVIEW,
  RequestBusinessPartnerStatusEnum.PENDING,
  RequestBusinessPartnerStatusEnum.CUSTOMER_REJECTED,
  RequestBusinessPartnerStatusEnum.REJECTED,
  RequestBusinessPartnerStatusEnum.CLOSED,
].join(',');

const JOIN_REQUESTS_STATUSES = [
  ServiceAgreementStatusEnum.PENDING,
  ServiceAgreementStatusEnum.DECLINED,
].join(',');

/** Create requests tab: open proxy Business Partner Requests. */
export function useCreateRequestsList({ page, limit, search, enabled }: ListQueryParams) {
  return useQuery({
    ...getV1RequestBusinessPartnerProxiesOptions({
      query: {
        page,
        limit,
        'filter[status]': { in: CREATE_REQUESTS_STATUSES },
        ...(search && { 'filter[name]': { startswith: search } }),
      },
    }),
    enabled,
    placeholderData: (previous) => previous,
  });
}

/** Join requests tab: outgoing pending/declined agreements with existing Customers. */
export function useJoinRequestsList({ page, limit, search, enabled }: ListQueryParams) {
  return useQuery({
    ...getV1ServiceAgreementsWithCustomerOptions({
      query: {
        page,
        limit,
        'filter[serviceAgreementStatus]': { in: JOIN_REQUESTS_STATUSES },
        scope: 'current',
        ...(search && { 'filter[customer.name]': { startswith: search } }),
      },
    }),
    enabled,
    placeholderData: (previous) => previous,
  });
}
